import { AlertCircle, Check } from "lucide-react";
import { Status, cn, focusRing } from "../../../components/ui";

/**
 * Where the user is in a product wizard: the step they are on and what it is for, the save state, and the steps they
 * may go back to. Shared by Add Product, Edit Product and ProductWizard.
 *
 * `title` names the wider task ("Edit Product") beside the step count; the heading is always the current step.
 * `unsavedLabel` is shown before the first save — Add Product passes one, because its draft does not exist yet; a
 * product that is already saved passes none, so it is never described as unsaved.
 */
export default function WizardHeader({
  steps,
  stepIndex,
  saving,
  error,
  warnings = [],
  lastSavedAt,
  stepStates = [],
  onStepClick,
  maxClickableIndex = stepIndex,
  title: headerTitle,
  unsavedLabel = null,
  contained = false,
}) {
  const current = steps?.[stepIndex];
  const warningList = Array.isArray(warnings) ? warnings : [];
  const skuConflictCount = warningList.filter((warning) =>
    /^SKU\s+"/i.test(String(warning || "").trim())
  ).length;

  const savedLabel = (() => {
    if (saving) return "Saving draft";
    if (error) return "Save failed";
    if (warningList.length > 0) {
      if (skuConflictCount > 0) {
        return `Draft saved · ${skuConflictCount} SKU conflict${skuConflictCount === 1 ? "" : "s"} ${skuConflictCount === 1 ? "needs" : "need"} attention`;
      }
      return `Draft saved with ${warningList.length} warning${warningList.length === 1 ? "" : "s"}`;
    }
    if (!lastSavedAt) return unsavedLabel;
    try {
      const dt = new Date(lastSavedAt);
      const t = dt.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" });
      return `Saved at ${t}`;
    } catch {
      return "Saved";
    }
  })();

  const saveTone = saving ? "neutral" : error ? "danger" : warningList.length > 0 ? "warning" : lastSavedAt ? "success" : "neutral";

  return (
    <div
      className={cn(
        "bg-surface-sunken p-4",
        contained ? "rounded-panel border border-subtle" : "border-b border-subtle"
      )}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="min-w-0">
          <p className="text-meta text-secondary">
            {headerTitle ? `${headerTitle} · ` : ""}Step {stepIndex + 1} of {steps.length}
          </p>
          <h2 className="mt-0.5 text-section-title text-foreground">{current?.label}</h2>
          {current?.description ? (
            <p className="mt-0.5 max-w-2xl text-body-sm text-secondary">{current.description}</p>
          ) : null}
        </div>

        {/* The live region stays mounted so a later "Saving draft" / "Saved at" is announced. */}
        <div role="status" aria-live="polite" className="shrink-0 text-meta sm:pt-0.5">
          {savedLabel ? (
            <Status tone={saveTone} icon={saveTone !== "neutral"} className={saveTone === "neutral" ? "text-secondary" : undefined}>
              {savedLabel}
            </Status>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="mt-2 text-meta text-danger" role="alert">
          {error}
        </p>
      ) : null}

      <div
        className="mt-3 h-1 w-full overflow-hidden rounded-full bg-hover"
        role="progressbar"
        aria-label="Product setup progress"
        aria-valuemin={0}
        aria-valuemax={steps.length}
        aria-valuenow={stepIndex + 1}
      >
        <div
          className="h-full rounded-full bg-accent transition-[width] duration-200 motion-reduce:transition-none"
          style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }}
        />
      </div>

      <nav aria-label="Product setup steps" className="mt-3 flex gap-2 overflow-x-auto pb-1">
        {steps.map((step, idx) => {
          const status = stepStates[idx]?.status || (idx === stepIndex ? "current" : "todo");
          const hint = stepStates[idx]?.hint;
          const isCurrent = idx === stepIndex;
          const isFuture = idx > maxClickableIndex;
          const isBlocked = status === "blocked" && !isFuture;
          const isDone = status === "done" && !isFuture;
          const canClick = typeof onStepClick === "function" && !isFuture && !isCurrent;
          const tone = isCurrent
            ? "border-accent bg-accent-subtle text-accent"
            : isFuture
              ? "border-subtle bg-surface text-tertiary"
              : "border-default bg-surface text-foreground";

          return (
            <button
              key={step.key}
              type="button"
              onClick={() => {
                if (canClick) onStepClick(idx);
              }}
              disabled={isFuture}
              aria-current={isCurrent ? "step" : undefined}
              aria-label={`${step.label}${isCurrent ? ", current step" : isFuture ? ", not visited yet" : isBlocked ? ", needs attention" : isDone ? ", complete" : ""}`}
              title={isFuture ? "Continue with Next to reach this step" : hint || step.label}
              className={cn(
                "flex min-h-11 min-w-36 flex-1 items-center gap-2 rounded-control border px-3 py-2 text-left text-control-sm transition-colors duration-150",
                "disabled:cursor-not-allowed",
                canClick ? "cursor-pointer hover:border-strong" : "cursor-default",
                tone,
                focusRing
              )}
            >
              <span
                className={cn(
                  "flex h-5 w-5 shrink-0 items-center justify-center text-meta font-semibold tabular-nums",
                  isBlocked ? "text-warning" : isDone ? "text-success" : undefined
                )}
                aria-hidden="true"
              >
                {isBlocked ? <AlertCircle className="h-4 w-4" /> : isDone ? <Check className="h-4 w-4" /> : idx + 1}
              </span>
              <span className="min-w-0">{step.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
